<script>
    import {PATH_PREFIX} from '../../apiUrls.js';

  import Stepper from '../Stepper/Stepper.svelte';
  import StepperNavigator from '../Stepper/StepperNavigator.svelte/';
  import { onMount, afterUpdate } from 'svelte';
  import {
    mechanismOptions,
    controlOptions,
    iconOptions,
    hazard as hazardStore,
    relatedActivities,
    control as controlStore
  } from './store.js';
  import Modal from '../Modal.svelte';
  import {validateForm} from '../Form/utils.js';
  import {get} from '../../utils.js';
  import DuplicateCheckTextInput from '../DuplicateCheckTextInput.svelte';
  import ComboSelect from '../ComboSelect/ComboSelect.svelte';
  import FocusQuestionInput from '../FocusQuestionInput.svelte';
  import HazardDetail from './HazardDetail.svelte';
  import RelatedActivities from './RelatedActivities.svelte';
  import Errors from '../Errors.svelte';
  import ControlInlineForm from './ControlInlineForm.svelte';
  import {submit} from '../Form/utils.js';
  import yup, {object, string, number} from 'yup';
  import {sgettext} from '../../i18n.js';

  export let title = sgettext('$$GLOBAL_RISK$$');
  export let id = 'globalHazardForm';
  export let duplicateCheckApi =
    `${PATH_PREFIX}/sw/api/globalhazard/check_duplicate/`;
  export let mechanismOptionApi = `${PATH_PREFIX}/sw/api/mechanisms/`;
  export let controlOptionApi =
    `${PATH_PREFIX}/sw/api/globalcontrols/`;
  export let iconOptionApi = `${PATH_PREFIX}/sw/api/icons/`;
  export let actionUrl = `${PATH_PREFIX}/sw/api/globalhazard/`;
  export let controlFormUrl = `${PATH_PREFIX}/sw/api/globalcontrol/`;
  export let relatedActivitiesApi = `${PATH_PREFIX}/sw/api/affected-activities/`;
  export let onSubmit = (data) => data;
  export let editId = 0;
  export let currentStep = 1;
  export let controlDuplicateCheckApi = `${PATH_PREFIX}/sw/api/globalcontrol/check_duplicate/`;

  let isValid = true;
  let showControlForm = false;
  let hazardNameBeforeEdit = '';
  let duplicateCheckHardFail = false;

  let schema = object().shape({
    name: string()
          .required(sgettext('$$REQUIRED_FIELD$$'))
          .test(
            sgettext("$$DUPLICATE_1$$"),
            sgettext("$$RISK_NAME_ALREADY_EXISTS$$"),
            _ => !duplicateCheckHardFail,
        ),
    mechanism_id: number()
      .required(sgettext('$$REQUIRED_FIELD$$'))
      .typeError(sgettext('$$REQUIRED_FIELD$$')),
    icon_id: number()
      .required(sgettext('$$REQUIRED_FIELD$$'))
      .typeError(sgettext('$$REQUIRED_FIELD$$'))
  });

  let controlSchema = object().shape({
    description: string().required(),
    name: string().required(),
    icon_id: number()
      .required(sgettext('$$REQUIRED_FIELD$$'))
      .typeError(sgettext('$$REQUIRED_FIELD$$'))
  });

  let errors = null;
  let sending = false;

  export let hazard = {
    name: '',
    mechanism_id: '',
    icon_id: '',
    focus_questions: [{ name: '' }],
    controls: [],
  };

  let steps = [
    {
      order: 1,
      label: sgettext('$$RISK_DETAIL$$'),
      done: false,
      isValid: true,
      fieldIsValid: {
        name: {
          isValid: true,
          errors: ''
        },
        mechanism_id: {
          isValid: true,
          errors: ''
        },
        icon_id: {
          isValid: true,
          errors: ''
        }
      }
    },
    {
      order: 2,
      label: sgettext('$$MANAGE_CONTROLS$$'),
      done: false,
      isValid: false,
      fieldIsValid: {
        description: {
          isValid: true,
          errors: ''
        },
        name: {
          isValid: true,
          errors: ''
        },
        icon_id: {
          isValid: true,
          errors: ''
        }
      }
    },
    {
      order: 3,
      label: sgettext('$$REVIEW$$'),
      done: false,
      isValid: false
    },
  ];

  let finalStep = 3;

  hazardStore.set(hazard);

  let hidePopup = true;
  let data = [];

  $: if (editId) {
    getEditItem();
  }

  $: {
      // ensure that a control can only be added once
      $hazardStore.controls = [...new Set($hazardStore.controls)];
  }

  async function handleFormValidation() {
    try{
      if(currentStep === 2 && showControlForm) {
        //validate control form
        await validateForm(controlSchema, $controlStore)
      } else if(currentStep === 1) {
        await validateForm(schema, $hazardStore);
        isValid = true;
        getRelatedActivities();
      }

      updateStepStatus(isValid);
      if(currentStep === finalStep){
        handleSave();
      } else {
        currentStep = currentStep + 1;
      }

      if(currentStep > finalStep){
        currentStep = finalStep;
      }
    } catch(err) {
      isValid = false;
      updateStepStatus(isValid);
      err.inner.forEach(er => {
        steps[currentStep-1].fieldIsValid[er.path].isValid = false;
        steps[currentStep-1].fieldIsValid[er.path].errors = er.message;
      });

    }
  }

  function updateStepStatus(valid) {
    const current = steps.filter(s => s.order === currentStep);
    if (current.length) {
      current[0].isValid = valid;
    }
  }

  function handleShowControlForm(){
      showControlForm = !showControlForm;
      if(!showControlForm) {
        isValid = true;
      }
  }

  function reset() {
    Object.keys(steps[0].fieldIsValid).forEach(k => {
      steps[0].fieldIsValid[k].isValid = true;
      steps[0].fieldIsValid[k].errors = '';
    })
    duplicateCheckHardFail = false;
  }

  function handleClose() {
    let hazard = {
      name: '',
      mechanism: '',
      icon_id: '',
      focus_questions: [{ name: '' }],
      controls: [],
    };

    let control = {
      description: '',
      icon_id: ''
    }

    hazardStore.set(hazard);
    controlStore.set(control);
    hidePopup = true;
    currentStep = 1;
    editId = 0;
    isValid = true;
    sending = false;
    showControlForm = false;
    errors = '';
  }

  function getHazardDetail() {
    const detail = {
      name: $hazardStore.name,
      icon: $iconOptions.filter((i) => i.id === $hazardStore.icon_id)[0],
      focus_questions: $hazardStore.focus_questions,
    };

    let energy = '';
    let mechanism = '';

    outerloop: for (let i = 0; i < $mechanismOptions.length; i++) {
      for (let j = 0; j < $mechanismOptions[i].mechanisms.length; j++) {
        if (
          $mechanismOptions[i].mechanisms[j].id ===
          $hazardStore.mechanism_id
        ) {
          energy = $mechanismOptions[i].name;
          mechanism = $mechanismOptions[i].mechanisms[j].name;
          detail.energy = energy;
          detail.mechanism = mechanism;
          break outerloop;
        }
      }
    }

    detail.controls = $controlOptions.filter((c) =>
      $hazardStore.controls.includes(c.id)
    );

    return detail;
  }


  function handleDuplicate(event) {
    isValid = true;
    duplicateCheckHardFail = false;
    if (event.detail.length) {
        if (event.detail[0].text !== hazardNameBeforeEdit && event.detail[0].similarity === 100) {
            isValid = false;
            duplicateCheckHardFail = true;
        }
    }
  }

  async function getEditItem() {
    if (editId) {
      const response = await get(actionUrl + editId + '/');
      const json = await response.json();

      if (json) {
        hazardNameBeforeEdit = json.name;
        json.controls = json.controls.filter(c => c)
        hazardStore.set(json);
      }

      return json;
    }
  }

  async function getMechanisms(url) {
    const response = await get(url);
    const json = await response.json();

    mechanismOptions.set(json);
  }

  async function getIcons(url) {
    const response = await get(url);
    const json = await response.json();

    iconOptions.set(json);
  }

  async function getControls(url) {
    const response = await get(url);
    const json = await response.json();

    controlOptions.set(json);
  }

  async function getRelatedActivities() {
    if(editId > 0) {
      const response = await get(`${relatedActivitiesApi}${editId}/`);
      const json = await response.json();

      relatedActivities.set(json);
    }
  }

  onMount(async () => {
    await getMechanisms(mechanismOptionApi);
    await getIcons(iconOptionApi);
    await getControls(controlOptionApi);

    jQuery(`#${id}`).on('show.bs.modal', function() {
      reset();
    });

    jQuery(`#${id}`).on('shown.bs.modal', function() {
      jQuery(`#name`).trigger('focus');
    });


    jQuery(`#${id}`).on('hidden.bs.modal', function() {
      handleClose();
    });
  });

  function handleSave() {
    sending = true;
    let method = 'POST';
    let url = actionUrl;
    if(editId) {
      method = 'PUT';
      url = actionUrl + editId + '/'
    }

    submit(url, method, getPayload())
      .then((data) => {
        onSubmit(data);
        sending = false;
      })
      .catch((error) => {
        errors = error;
        sending = false;
      });
  }

  function getPayload(){
    const payload = {...$hazardStore, focus_questions: []};

    if($hazardStore.focus_questions){
      $hazardStore.focus_questions.forEach(q => {
        if(q.hasOwnProperty('name') && q.name) {
          payload.focus_questions.push(q);
        }
      });
    }

    return payload;
  }


  function handleNewControlCreation(data) {
    controlOptions.update(options => [...options, data]);
    $hazardStore.controls = [...$hazardStore.controls, data.id];
  }
</script>

<style>
  form :global(label) {
    font-weight: 500;
  }
  .footer {
    width: 100%;
  }

  .btn-link {
    width: fit-content;
    color: #656565;
    cursor: pointer;
  }

  .btn-link:focus {outline: none; color: #dd5900;}

  .btn-link:hover {
    color: #dd5900;
    text-decoration: none;
  }
</style>

<Modal
  {id}
  {title}
  onCancel={handleClose}
  onSave={handleSave}
  backdrop={'static'}
  large={true}>
  <div slot="body">
    <form>
      <Stepper {steps} bind:currentStep />

      {#if currentStep === 1}
        <div class="form-group">
          <DuplicateCheckTextInput
            id="name"
            required="true"
            apiurl={duplicateCheckApi}
            label={sgettext("$$NAME_2$$")}
            message={sgettext("$$SIMILAR_RISK_FOUND$$")}
            bind:value={$hazardStore.name}
            bind:isValid={steps[0].fieldIsValid.name.isValid}
            bind:errors={steps[0].fieldIsValid.name.errors}
            on:check={handleDuplicate}
            {schema} />
        </div>
        <div class="form-row">
          <div class="form-group col-md-6">
            <ComboSelect
              id="mechanism_id"
              label={sgettext("$$MECHANISM_FOCUS_AREA$$")}
              bind:options={$mechanismOptions}
              children="mechanisms"
              bind:value={$hazardStore.mechanism_id}
              required="true"
              bind:isValid={steps[0].fieldIsValid.mechanism_id.isValid}
              bind:errors={steps[0].fieldIsValid.mechanism_id.errors}
              searchable={true}
              {schema} />
          </div>
          <div class="form-group col-md-6">
            <ComboSelect
              id="icon_id"
              label={sgettext("$$ICON_1$$")}
              options={$iconOptions}
              searchable={true}
              bind:value={$hazardStore.icon_id}
              bind:isValid={steps[0].fieldIsValid.icon_id.isValid}
              bind:errors={steps[0].fieldIsValid.icon_id.errors}
              {schema}
              />
          </div>
        </div>
        <div class="form-group">
          <FocusQuestionInput
            id="focusQuestionInput"
            bind:value={$hazardStore.focus_questions} />
        </div>
      {:else if currentStep === 2}
        <div class="form-group">
          <ComboSelect
            id="controls"
            label={sgettext("$$CHOOSE_EXISTING_CONTROLS$$")}
            options={$controlOptions}
            multiple={true}
            searchable={true}
            bind:value={$hazardStore.controls} />

          <br />
        </div>
        <div class="btn-link" on:click={handleShowControlForm}>
          {#if showControlForm}
            {sgettext('$$CLICK_HIDE_NEW_CONTROL_FORM$$')}
          {:else}
            {sgettext('$$CLICK_CREATE_NEW_CONTROL$$')}
          {/if}
        </div>
        {#if showControlForm}
          <ControlInlineForm
            onSubmit={handleNewControlCreation}
            schema={controlSchema}
            bind:show={showControlForm}
            actionUrl={controlFormUrl}
            duplicateCheckApi={controlDuplicateCheckApi}
            bind:fieldIsValid={steps[1].fieldIsValid}/>
        {/if}
      {:else}
        <div class="form-group">
          <HazardDetail hazard={getHazardDetail()} />
          {#if $relatedActivities.hasOwnProperty('results') && $relatedActivities.results.length}
          <RelatedActivities data={$relatedActivities} />
          {/if}
        </div>
      {/if}
      {#if errors}
        <Errors {errors} />
      {/if}
    </form>
  </div>

  <div slot="footer" class="footer">
    <StepperNavigator
      bind:currentStep
      {isValid}
      {sending}
      back={() => errors = null}
      next={handleFormValidation} />
  </div>
</Modal>
